import React, { useMemo, type Dispatch, type SetStateAction } from "react";

import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { Checkbox } from "@shared/checkbox";

import { usePerformanceTrackingLiveForecastData } from "../../../hooks/use-performance-tracking-live-forecast";
import {
  ACTUAL_ID,
  ACTUAL_LEGEND,
  LIVE_FORECAST_ID,
  LIVE_FORECAST_LEGEND,
  PLANNING_TARGET_ID,
  PLANNING_TARGET_LEGEND,
  TARGET_RANGE_COLOR,
  TARGET_RANGE_ID,
} from "../const";

export function TrackingChartLegend({
  datasetVisibility,
  setDatasetVisibility,
}: {
  datasetVisibility: Record<string, boolean>;
  setDatasetVisibility: Dispatch<SetStateAction<Record<string, boolean>>>;
}) {
  const { adoptedClientSetting } = useClientSettings();
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const { kpis: clientKpis } = useClientKpis();
  const { kpi: adoptedClientSettingKpi } =
    useClientSettingData(adoptedClientSetting);

  const { enableMMMLiveForecasting } = usePerformanceTrackingLiveForecastData();

  const kpiName = useMemo(() => {
    let name = adoptedClientSettingKpi?.friendly_name;

    if (!name) {
      name = clientKpis.find(
        (e) => e.kpi_name === adoptedBudgetMix?.data.kpi_name,
      )?.friendly_name;
    }

    return name;
  }, [
    adoptedClientSettingKpi?.friendly_name,
    clientKpis,
    adoptedBudgetMix?.data.kpi_name,
  ]);

  const toggleDatasetVisibility =
    (datasetId: keyof typeof datasetVisibility) => (checked: boolean) =>
      setDatasetVisibility((prev: typeof datasetVisibility) => ({
        ...prev,
        [datasetId]: checked,
      }));

  return (
    <div className="flex justify-between text-xs space-x-5">
      <label className="flex items-center space-x-0.5 cursor-pointer">
        <Checkbox
          className="mr-2"
          value={datasetVisibility[ACTUAL_ID]}
          onChange={toggleDatasetVisibility(ACTUAL_ID)}
        />
        <div dangerouslySetInnerHTML={{ __html: ACTUAL_LEGEND }} />
        <div>Actual {kpiName}</div>
      </label>
      {enableMMMLiveForecasting && (
        <label className="flex items-center space-x-0.5 cursor-pointer">
          <Checkbox
            className="mr-2"
            value={datasetVisibility[LIVE_FORECAST_ID]}
            onChange={toggleDatasetVisibility(LIVE_FORECAST_ID)}
          />
          <div dangerouslySetInnerHTML={{ __html: LIVE_FORECAST_LEGEND }} />
          <div>Projected {kpiName}</div>
        </label>
      )}
      <label className="flex items-center space-x-0.5 cursor-pointer">
        <Checkbox
          className="mr-2"
          value={datasetVisibility[PLANNING_TARGET_ID]}
          onChange={toggleDatasetVisibility(PLANNING_TARGET_ID)}
        />
        <div dangerouslySetInnerHTML={{ __html: PLANNING_TARGET_LEGEND }} />
        <div>
          {kpiName} (Target, modelled weekly but distributed evenly on a daily
          basis)
        </div>
      </label>
      <label className="flex items-center space-x-0.5 cursor-pointer">
        <Checkbox
          className="mr-2"
          value={datasetVisibility[TARGET_RANGE_ID]}
          onChange={toggleDatasetVisibility(TARGET_RANGE_ID)}
        />
        <div
          style={{
            backgroundColor: TARGET_RANGE_COLOR,
            height: 15,
            width: 30,
          }}
        />
        <div>{TARGET_RANGE_ID}</div>
      </label>
    </div>
  );
}
